'use client';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@prismo-io/design-system';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { type FC, useMemo } from 'react';

type Props = {
  itemName1: string;
  itemName2: string;
  itemName3: string;
  color?: string;
  isBlury?: boolean;
  needPosition?: boolean;
};

// Register the required components
echarts.use([GridComponent, BarChart, SVGRenderer]);

const limitedString = (text: string) => {
  return text.length > 50 ? `${text.substring(0, 50)}...` : text;
};

export const Podium: FC<Props> = (props: Props) => {
  const {
    itemName1,
    itemName2,
    itemName3,
    isBlury = false,
    needPosition = true,
  } = props;

  const theme = useTheme<Theme>();

  const data = useMemo(
    () => [
      {
        name: isBlury ? 'Gloire' : limitedString(itemName2),
        value: 50,
      },
      {
        name: isBlury ? 'Amour' : limitedString(itemName1),
        value: 70,
      },
      {
        name: isBlury ? 'Beauté' : limitedString(itemName3),
        value: 30,
      },
    ],
    [isBlury, itemName1, itemName2, itemName3]
  );

  const option = useMemo(
    () => ({
      grid: {
        left: 0,
        right: 28,
        top: 0,
        bottom: 0,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map((item) => item.name),
        axisLabel: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        show: false,
      },
      series: [
        {
          type: 'bar',
          data: data.map((item) => item.value),
          label: {
            show: true,
            color: '#2D5B5B',
            position: 'top',
            fontWeight: 'bolder',
            formatter: '{b}',
            width: 110,
            overflow: 'break',
          },
          itemStyle: {
            color: theme.palette.primary.main,
            // backgroundImage: theme.palette.gradient.main,
          },
        },
      ],
    }),
    [data, theme]
  );

  return (
    <Box
      component="div"
      position="relative"
      width={{ none: '300px', sx: '500px' }}
      height="300px"
      sx={{ pointerEvents: 'none' }}
    >
      <Box
        component="div"
        position="absolute"
        left="50%"
        width="160px"
        height="160px"
        borderRadius="50%"
        sx={{
          transform: 'translateX(-50%)',
          // backgroundColor: theme.palette.primary.main,
          backgroundImage: theme.palette.gradient.main,
          opacity: 0.15,
        }}
      />
      {needPosition && (
        <>
          <Box
            component="div"
            position="absolute"
            bottom="35%"
            left="22.5%"
            sx={{
              transform: 'translateX(-50%)',
            }}
            zIndex={50}
          >
            <Typography color="white" fontWeight="bolder" fontSize="1rem">
              2
            </Typography>
          </Box>
          <Box
            component="div"
            position="absolute"
            bottom="56%"
            left="50%"
            sx={{
              transform: 'translateX(-50%)',
            }}
            zIndex={50}
          >
            <Typography color="white" fontWeight="bolder" fontSize="1rem">
              1
            </Typography>
          </Box>
          <Box
            component="div"
            position="absolute"
            bottom="17%"
            right="20%"
            sx={{
              transform: 'translateX(-50%)',
            }}
            zIndex={50}
          >
            <Typography color="white" fontWeight="bolder" fontSize="1rem">
              3
            </Typography>
          </Box>
        </>
      )}
      <ReactEChartsCore
        echarts={echarts}
        option={option}
        notMerge={true}
        lazyUpdate={true}
        theme={'theme_name'}
        style={{ height: '100%', width: '100%', position: 'relative' }}
        opts={{}}
      />
    </Box>
  );
};
